import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { firstValueFrom } from 'rxjs';
import { auth } from '../app/app.config';
import { ErrorReportingService } from './error-reporting.service';

type AuthenticationStatus = 'anonymous' | 'authenticated' | 'unknown';

interface Authentication {
  status: AuthenticationStatus;
  userSession?: UserSession;
}

interface UserSession {
  email: string;
  expiresAt: Date;
  id: string;
  lastName: string;
  name: string;
  roles: string[];
  uid: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  authentication = signal<Authentication>({ status: 'unknown' });
  private errorReportingService = inject(ErrorReportingService);
  private http = inject(HttpClient);
  private pendingSessionCheck: Promise<boolean> | undefined;

  async isAuthenticated(): Promise<boolean> {
    if (this.authentication().status !== 'unknown') {
      return this.authentication().status === 'authenticated';
    }
    if (!this.pendingSessionCheck) {
      this.pendingSessionCheck = this.checkSession();
    }
    return this.pendingSessionCheck;
  }

  async sendPasswordRecovery(data: { email: string; }): Promise<void> {
    await firstValueFrom(this.http.post<void>('/api/auth/password-recovery', data));
  }

  async signIn(email: string, password: string): Promise<void> {
    let step = '';
    try {
      step = 'sign-in-with-email-and-password';
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      step = 'get-id-token';
      const idToken = await userCredential.user.getIdToken();
      step = 'request-otp';
      await firstValueFrom(this.http.post<void>('/api/auth/request-otp', null, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }));
    } catch (error) {
      this.errorReportingService.log('AuthService.signIn()', step, error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    await firstValueFrom(this.http.post<void>('/api/auth/sign-out', null)).catch();
    this.authentication.set({ status: 'anonymous' });
    this.pendingSessionCheck = undefined;
  }

  async verifyOtp(secret: string): Promise<void> {
    let step = '';
    try {
      step = 'get-current-user';
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user');
      }
      step = 'get-id-token';
      const idToken = await currentUser.getIdToken();
      step = 'verify-otp';
      const { data: userSession } = await firstValueFrom(this.http.post<{ data: UserSession; }>('/api/auth/verify-otp', { secret }, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }));
      this.authentication.set({
        status: 'authenticated',
        userSession: {
          ...userSession,
          expiresAt: new Date(userSession.expiresAt),
        },
      });
    } catch (error) {
      this.errorReportingService.log('AuthService.verifyOtp()', step, error);
      throw error;
    }
  }

  private async checkSession(): Promise<boolean> {
    try {
      const { data: userSession } = await firstValueFrom(this.http.get<{ data: UserSession; }>('/api/auth/session'));
      this.authentication.set({
        status: 'authenticated',
        userSession: {
          ...userSession,
          expiresAt: new Date(userSession.expiresAt),
        },
      });
      return true;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.authentication.set({ status: 'anonymous' });
        return false;
      }
      throw error;
    } finally {
      this.pendingSessionCheck = undefined;
    }
  }
}
